import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { convertUnixtTimeToDateTime } from '../../lib/tools';
import ColorSF2 from '../../styles/common/ColorSF2';

const Contents = styled.div`
  width: 100%;
  height: 100%;
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;
const ScrolledBlock = styled.div`
  width: 100%;
  height: 410px;
  overflow-x: hidden;
  overflow-y: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
`;
const Thead = styled.thead`
  background-color: ${ColorSF2.paleBlue};
`;
const Tbody = styled.tbody`
  background-color: ${ColorSF2.white};
`;
const TrHead = styled.tr`
  text-decoration: none;
  cursor: pointer;
  height: 27px;
  font-size: 12px;
`;
const TrBody = styled.tr`
  height: 40px;
  border-bottom: 1px solid ${ColorSF2.mediumGray};
  text-decoration: none;
`;
const Th = styled.th`
  padding-left: 10px;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  position: relative;
  @media (max-height: 530px) {
    font-size: 12px;
  }
`;
const ThCenter = styled(Th)`
  text-align: center;
`;
const Td = styled.td`
  padding-left: 10px;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  position: relative;
  font-size: 14px;
  font-weight: 300;
  @media (max-height: 530px) {
    font-size: 12px;
  }
`;
const ThDummy = styled.th`
  width: 7px;
`;
const TdDummy = styled.td`
  width: 0px;
`;
const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
`;
const Button = styled.button`
  width: 66px;
  height: 24px;
  border-radius: 4px;
  font-size: 12px;
  &:hover {
    opacity: 0.8;
  }
`;
const EditButton = styled(Button)`
  background-color: ${ColorSF2.navyBlue};
  color: ${ColorSF2.white};
  font-weight: bold;
  border: none;
  &:hover {
    background-color: ${ColorSF2.navyBlue};
  }
`;
const RemoteButton = styled(Button)`
  background-color: ${props =>
    (props.disabled && ColorSF2.mediumGray) ||
    (props.isTalking && ColorSF2.red) ||
    ColorSF2.green};
  color: ${ColorSF2.white};
  border: 0px;
  &:hover {
    opacity: ${props => (props.disabled ? 1 : 0.8)};
    background-color: ${props =>
      (props.disabled && ColorSF2.mediumGray) ||
      (props.isTalking && ColorSF2.red) ||
      ColorSF2.green};
  }
`;

const List = ({ isGroup, device, list, edit, support, onRemoteSupportEnd }) => (
  <Contents>
    <Table>
      <Thead>
        <TrHead>
          <Th>
            <FormattedMessage id="blackboardList.item.title" />
          </Th>
          <Th>
            <FormattedMessage id="blackboardList.item.subtitle" />
          </Th>
          <Th>
            <FormattedMessage id="blackboardList.item.operatorName" />
          </Th>
          <ThCenter>
            <FormattedMessage id="blackboardList.item.createdAt" />
          </ThCenter>
          <ThCenter>
            <FormattedMessage id="blackboardList.item.action" />
          </ThCenter>
          <ThDummy />
        </TrHead>
      </Thead>
    </Table>
    <ScrolledBlock id="canScrollTableBlackboard">
      <Table>
        <Tbody>
          {list &&
            list.map((item, idx) => {
              const key = `boardlist-${idx}`;
              const createdAt = convertUnixtTimeToDateTime(item.createdAt);
              // TODO 複数人通話の場合、遠隔支援終了のパラメータ確認
              return (
                <TrBody id={key} key={key}>
                  <Td>{item.title}</Td>
                  <Td>{item.subtitle}</Td>
                  <Td>{item.operatorName}</Td>
                  <Td>{createdAt}</Td>
                  <Td>
                    <ActionsWrapper>
                      <EditButton
                        id={`editBoard-${idx}`}
                        className="clickableItem"
                        role="presentation"
                        onClick={() => edit(item.id)}
                      >
                        <FormattedMessage id="blackboardList.item.action.detail" />
                      </EditButton>
                      {(!isGroup && !device.loginStatus && (
                        <RemoteButton disabled>
                          <FormattedMessage id="blackboardList.item.action.remoteSupport" />
                        </RemoteButton>
                      )) ||
                        ((isGroup || device.isRemoteSupport) && (
                          <RemoteButton
                            id={`remoteBoard-${idx}`}
                            className="clickableItem"
                            role="presentation"
                            onClick={() => support(item.id)}
                          >
                            <FormattedMessage id="blackboardList.item.action.remoteSupport" />
                          </RemoteButton>
                        )) || (
                          <RemoteButton
                            onClick={() => onRemoteSupportEnd(device.deviceId)}
                            isTalking
                          >
                            <FormattedMessage id="blackboardList.item.action.remoteSupportEnd" />
                          </RemoteButton>
                        )}
                    </ActionsWrapper>
                  </Td>
                  <TdDummy />
                </TrBody>
              );
            })}
        </Tbody>
      </Table>
    </ScrolledBlock>
  </Contents>
);

List.propTypes = {
  isGroup: PropTypes.bool.isRequired,
  device: PropTypes.object.isRequired,
  list: PropTypes.array.isRequired,
  edit: PropTypes.func.isRequired,
  support: PropTypes.func.isRequired,
  onRemoteSupportEnd: PropTypes.func.isRequired,
};

export default List;
